<template>
  <DetailsSection
    v-if="hasChildren"
    :label="birthdayLabel"
    info-message="Kinder ab 12 Jahre zum Zeitpunkt des Fluges gelten bei den meisten Fluggesellschaften als vollzahlende Erwachsene."
  >
    <template 
      v-for="(child, index) in (paxConfig.children + paxConfig.babies)"
      :key="index"
    >
      <TextField
        :label="'Kind ' + child"
        placeholder="tt.mm.jjjj"
        :field-name="'child-' + index"
        :field-value="getChildsBirthdate('child-' + index)"
        :value-changed="setChildsBirthdate"
        class="mb-1"
      />
    </template>
  </DetailsSection>

  <DetailsSection
    label="Suche"
    class="mt-2"
  >
    <div class="row">
      <div class="col">
        <DropdownField
          field-name="gateway"
          :field-value="gatewayKey"
          :value-changed="selectAirport"
          :item-list="gatewayItems"
          :disabled="!availableGateways"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <DropdownField
          class="mt-2"
          field-name="direction"
          :field-value="flightDirectionKey"
          :value-changed="selectFlightDirection"
          :item-list="flightDirectionItems"
          :disabled="!selectedFilters.gateway"
        />
      </div>
      <div class="col">
        <DropdownField
          class="mt-2"
          field-name="transportationClass"
          :field-value="transportationClassKey"
          :value-changed="selectTransportationClass"
          :item-list="transportationClassItems"
          :disabled="!selectedFilters.gateway"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <TagsField
          class="mt-2"
          field-name="airlines"
          :field-value="selectedFilters.airlines"
          :value-changed="setAirlines"
          :item-list="airlineItems"
          :max-tags="3"
          placeholder="max. 3 Fluggesellschaften"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <Checkbox
          class="modal-checkbox"
          label="Direktflug"
          :checked="selectedFilters.nonstop"
          :clicked="setNonstop"
        />
      </div>
    </div>
    
    <div class="row">
      <div class="col mt-1 color-aida-red flex-flight-message">
        <span v-if="!!flexFlightMessage">
          {{ flexFlightMessage }}
        </span>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <ExpiButton
          :is-disabled="!isFlexFlightSearchEnabled || isFlexFlightLoading"
          :is-busy="!!isFlexFlightSearchEnabled && isFlexFlightLoading"
          :click="findFlexFlights"
          label-id="Wunschflüge finden"
        />
      </div>
    </div>
  </DetailsSection>

  <DetailsSection
    label="verfügbare Wunschflüge"
    class="border-top-0"
  >
    <DropdownField
      field-name="selected-flight"
      :field-value="flightKey"
      :value-changed="selectFlight"
      :item-list="flightItems"
      :disabled="!availableFlights.length"
    />

    <div 
      v-if="!!selectedFlight"
      class="container mt-3"
    >
      <div class="row">
        <div class="col">
          <FlightPanel 
            :flight="selectedFlight?.outboundFlight" 
            direction="O" 
          />
        </div>
        <div class="col">
          <FlightPanel 
            :flight="selectedFlight?.inboundFlight" 
            direction="I"
          />
        </div>
      </div>
    </div>
  </DetailsSection>

  <DetailsSection
    label="Bahnfahrt zum/ vom Flughafen"
    type="last"
    :info-message="hasChildren ? 'Kinder bis einschließlich 14 Jahre reisen kostenfrei mit der Bahn' : ''"
    class="mt-2"
  >
    <ToggleGroup
      :options="getRailOptions()"
      :select="selectRailDirection"
      :selected-key="railDirection"
      class="d-flex justify-content-stretch"
    />

    <radio-buttons
      v-if="isRailValid" 
      :items="selectableRailOptionsByDirection" 
      :selected-item="selectedRailOptionId"
      :select="selectRailOption"
      :displayed-label="categoryDescription"
    />
    <div 
      v-if="railErrorMessage != ''" 
      class="w-100 text-center my-2 fs-16 color-sachlich-grey"
    >
      {{ railErrorMessage }}
    </div>
  </DetailsSection>
</template>

<script lang="ts">
import { Direction } from '@/bewotec/openapi'
import RadioButtons from '@/components/common/RadioButtons.vue'
import { generateRequestId } from '@/general/helper'
import { useFilterStore, useFlexFlightStore } from '@/store'
import {
    FlightItem,
    IFlightDirectionItem,
    IGatewayItem,
    ITransportationClassItem,
    SelectableRailOptionsFlexFlight
} from '@/store/details/flex-flight/interfaces'
import { RailDirection, RailLabel } from '@/store/details/rail/interfaces'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { convertToExpitecDirection, getRailLabel } from '../../../store/details/rail/helper'
import Checkbox from '../../common/Checkbox.vue'
import DropdownField from '../../common/DropdownField.vue'
import ExpiButton from '../../common/ExpiButton.vue'
import TagsField from '../../common/TagsField.vue'
import TextField from '../../common/TextField.vue'
import ToggleGroup from '../../common/ToggleGroup.vue'
import DetailsSection from '../DetailsSection.vue'
import FlightPanel from './FlightPanel.vue'

export default defineComponent({
  components: {
    DetailsSection,
    DropdownField,
    ToggleGroup,
    FlightPanel,
    Checkbox,
    TextField,
    TagsField,
    ExpiButton,
    RadioButtons,
  },

  computed: {
    ...mapState(useFilterStore, {
      paxConfig: store => store.paxConfig
    }),
    ...mapState(useFlexFlightStore, {
      gatewayItems: store => store.gatewayItems,
      gatewayKey: store => store.gatewayKey,
      flightDirectionKey: store => store.flightDirectionKey,
      transportationClassKey: store => store.transportationClassKey,
      flightKey: store => store.flightKey,
      isNoRailAllowed: store => store.isNoRailAllowed,
      railErrorMessage: store => store.getRailErrorMessage,
      flightItems: store => store.flightItems,
      flightDirectionItems: store => store.flightDirectionItems,
      transportationClassItems: store => store.transportationClassItems,
      airlineItems: store => store.airlineItems,
      isFlexFlightSearchEnabled: store => store.isFlexFlightSearchEnabled,
      availableGateways: store => store.availableGateways,
      availableFlights: store => store.availableFlights,
      selectedFlight: store => store.selectedFlight,
      flexFlightMessage: store => store.flexFlightMessage,
      railDirection: store => store.railDirection,
      selectedRailOption: store => store.selectedRailOption,
      selectableRailOptions: store => store.selectableRailOptions,
      isFlexFlightLoading: store => store.isLoading
    }),
    ...mapWritableState(useFlexFlightStore, {
      birthdates: 'birthdates',
      requestId: 'requestId',
      selectedFilters: 'selectedFilters',
      selectedRailOptionId: 'selectedRailOptionId',
    }),
    
    hasChildren(): boolean {
      return !!this.paxConfig.children || !!this.paxConfig.babies
    },
    isRailValid() {
      return this.railDirection !== 'none' && !('error' in this.selectableRailOptions)
    },
    railLabel(): RailLabel {
      return getRailLabel(this.railDirection)
    },
    selectableRailOptionsByDirection() {
      const selectableRailOptions = this.selectableRailOptions
      const isNotError = !('error' in selectableRailOptions)
      const direction = convertToExpitecDirection(this.railDirection)

      return isNotError ? selectableRailOptions[direction] : []
    },

    birthdayLabel() {
      return this.paxConfig.children + this.paxConfig.babies > 1 ? 'Geburtsdaten der Kinder' : 'Geburtsdatum des Kindes'
    }
  },

  mounted() {
    this.resetBirthdates()
  },

  methods: {
    ...mapActions(useFlexFlightStore, {
      resetBirthdates: 'resetBirthdates',
      validateMandatoryFields: 'validateMandatoryFields',
      fetchAvailableFlights: 'fetchAvailableFlights',
      resetAvailableFlights: 'resetAvailableFlights',
      selectGateway: 'selectGateway',
      selectFlexFlightDirection: 'selectFlightDirection',
      selectFlexTransportationClass: 'selectTransportationClass',
      selectDefaultRailOption: 'selectDefaultRailOption',
      setInitialRNFDirection: 'setInitialRNFDirection',
      setRailOption: 'selectRailOption',
      selectRNFRailDirection: 'selectRNFRailDirection',
      setFlexAirlines: 'setAirlines',
      fetchRailOptions: 'fetchRailOptions',
      resetRail: 'resetRail',
      setFlight: 'selectFlight'
    }),
    setChildsBirthdate(fieldName: string, fieldValue: string) {
      this.birthdates[fieldName] = fieldValue
      this.validateMandatoryFields()
    },
    getChildsBirthdate(fieldName: string) {
      return this.birthdates[fieldName]
    },

    selectAirport(_: any, __: any, item: IGatewayItem) {
      const gateway = this.availableGateways.find(gateway => gateway.locCd === item.id)

      this.selectGateway(gateway)
      this.validateMandatoryFields()
      this.resetAvailableFlights()
    },
    selectFlightDirection(_: any, __: any, item: IFlightDirectionItem | undefined) {
      this.selectFlexFlightDirection(item)
      this.validateMandatoryFields()
    },
    selectTransportationClass(_: any, __: any, item: ITransportationClassItem | undefined) {
      this.selectFlexTransportationClass(item)
      this.validateMandatoryFields()
    },
    setAirlines(_: any, items: string[]) {
      this.setFlexAirlines(items)
    },
    setNonstop() {
      this.selectedFilters.nonstop = !this.selectedFilters.nonstop
    },
    findFlexFlights() {
      this.requestId = generateRequestId('FLX')
      this.fetchAvailableFlights()
      this.resetRail()
    },
    async selectFlight(_: any, __: any, item: FlightItem) {
      this.setFlight(item)
      await this.fetchRailOptions()
      this.setInitialRNFDirection()
      this.selectDefaultRailOption()
    },

    getRailOptions() {
      const selectedFlightDirection = this.selectedFlight?.direction
      const isBothWays = selectedFlightDirection === Direction.RT || selectedFlightDirection === Direction.OJ
      const selectableRailOptions = this.selectableRailOptions
      const isNotError = !('error' in selectableRailOptions)

      return [
        {
          key: 'none',
          label: 'keine Bahnfahrt',
          available: this.isNoRailAllowed,
        },
        {
          key: 'both',
          label: 'Hin- und Rückfahrt',
          available: 
            isNotError && 
            isBothWays && 
            !!selectableRailOptions[Direction.RT]?.length,
        },
        {
          key: 'outbound',
          label: 'Hinfahrt',
          available: 
            isNotError && 
            (isBothWays || selectedFlightDirection === Direction.O) && 
            !!selectableRailOptions[Direction.O]?.length,
        },
        {
          key: 'inbound',
          label: 'Rückfahrt',
          available: 
            isNotError && 
            (isBothWays || selectedFlightDirection === Direction.R) && 
            !!selectableRailOptions[Direction.R]?.length,
        },
      ]
    },
    selectRailDirection(option: RailDirection) {
        this.selectRNFRailDirection(option)
        this.selectRailOption(this.selectedRailOptionId!)
    },
    selectRailOption(optionId: string) {
      this.selectedRailOptionId = optionId
      this.setRailOption(optionId)
    },

    categoryDescription: (option: SelectableRailOptionsFlexFlight) => {
      if (!!option && 'railClassName' in option) return option.railClassName

      return ''
    },
  },
})
</script>

<style lang="scss" scoped>
.flex-flight-message {
  height: 24px;
}
</style>
